import PropTypes from 'prop-types';
import Hooks from 'core/presentation/hooks';
import UIViewUtils from 'core/presentation/ui-view/utils';
import { routingProps } from 'core/constants/routing-props.js';
import { useMemo } from 'react';

/**
 * UIControllerView HoC function is used to wrap component implementation
 * with basic mojito functionality and tooling and provides
 * specific data loading functionality with `controllerHelper` object that will be passed down
 * to implementation through props.
 *
 * @function UIControllerView
 * @memberof Mojito.Core.Presentation
 *
 * @param {string} name - ControllerView name.
 * @param {object} defaultImpl - Default implementation class.
 * @param {object} [propTypes] - ControllerView prop types.
 * @param {object} [defaultProps = {}] - ControllerView default prop values.
 *
 * @returns {Function} MojitoController function.
 */
export default function UIControllerView(name, defaultImpl, propTypes, defaultProps = {}) {
    const mojitoPropTypes = {
        ...routingProps,
        ...propTypes,
        config: PropTypes.object,
    };

    const MojitoController = props => {
        let componentProps = UIViewUtils.ensureDefaultProps(props, defaultProps);
        componentProps = UIViewUtils.sanitizeRouteProps(componentProps);
        const appContext = Hooks.useAppContext();
        const Implementation =
            Hooks.useImplementation(name, appContext.uiContextPath) || defaultImpl;
        const mojitoTools = Hooks.useComponentTools(
            name,
            props.config,
            Implementation,
            appContext,
            Hooks.useEmitPerformanceMark()
        );
        const controllerHelper = Hooks.useControllerHelper(mojitoTools.instanceId);
        Hooks.usePropTypesValidation(componentProps, mojitoPropTypes, name);
        const controllerTools = useMemo(
            () => ({ ...mojitoTools, controllerHelper }),
            [controllerHelper, mojitoTools]
        );

        return <Implementation {...componentProps} mojitoTools={controllerTools} />;
    };

    MojitoController.propTypes = mojitoPropTypes;
    MojitoController.displayName = `${name}UIControllerWrapper`;

    return MojitoController;
}
